import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  Calendar,
  Menu,
  X,
  Settings,
  User,
  Bell,
  Wifi,
  WifiOff,
  RefreshCw,
  Shield,
  LogIn,
  LogOut,
} from 'lucide-react';

import { useAppStore } from '../../stores/appStore';
import { useWebSocket } from '../../hooks/useWebSocket';
// PWA disabled
// import { usePushNotifications } from '../../hooks/usePWA';
import DateSelector from '../Common/DateSelector';

const Header = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDateSelectorOpen, setIsDateSelectorOpen] = useState(false);

  const {
    selectedDate,
    setSelectedDate,
    connectionStatus,
    user,
    reminders,
  } = useAppStore();

  const { isConnected, reconnect } = useWebSocket();
  const isSubscribed = true; // PWA disabled

  const handleDateChange = (date: Date) => {
    setSelectedDate(date);
    setIsDateSelectorOpen(false);
    if (location.pathname !== '/') {
      navigate('/');
    }
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleReconnect = () => {
    reconnect();
  };

  const isAdmin = user?.role === 'ADMIN' || user?.role === 'MODERATOR';

  const menuItems = [
    {
      label: t('navigation.home'),
      path: '/',
      icon: Calendar,
    },
    ...(isAdmin ? [{
      label: 'Admin',
      path: '/admin',
      icon: Shield,
    }] : []),
    ...(user ? [{
      label: t('navigation.profile'),
      path: '/profile',
      icon: User,
    }] : []),
    ...(user ? [{
      label: t('navigation.settings'),
      path: '/settings',
      icon: Settings,
    }] : []),
    ...(!user ? [{
      label: 'Iniciar sesión',
      path: '/login',
      icon: LogIn,
    }] : []),
  ];

  const activeReminderCount = reminders.size;

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-gray-200 safe-area-top">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <motion.div
            className="flex items-center space-x-2 cursor-pointer"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate('/')}
          >
            <div className="w-8 h-8 bg-gradient-manga rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-lg">P</span>
            </div>
            <span className="text-xl font-bold gradient-text hidden sm:block">
              PRO-Act
            </span>
          </motion.div>

          {/* Date Selector - Center */}
          <div className="flex-1 flex justify-center">
            <motion.button
              className="flex items-center space-x-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500"
              onClick={() => setIsDateSelectorOpen(true)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Calendar className="w-4 h-4 text-gray-600" />
              <span className="text-sm font-medium text-gray-800">
                {format(selectedDate, 'MMM d', { locale: es })}
              </span>
            </motion.button>
          </div>

          {/* Right Actions */}
          <div className="flex items-center space-x-3">
            {/* Connection Status */}
            <motion.div
              className="relative"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 300 }}
            >
              {connectionStatus === 'online' ? (
                <Wifi className="w-5 h-5 text-green-500" />
              ) : connectionStatus === 'reconnecting' ? (
                <RefreshCw className="w-5 h-5 text-yellow-500 animate-spin" />
              ) : (
                <motion.button
                  onClick={handleReconnect}
                  className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <WifiOff className="w-5 h-5 text-red-500" />
                </motion.button>
              )}
            </motion.div>

            {/* Notification Bell */}
            <motion.button
              className="relative p-2 hover:bg-gray-100 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/profile')}
            >
              <Bell className="w-5 h-5 text-gray-600" />
              {activeReminderCount > 0 && (
                <motion.span
                  className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', stiffness: 300 }}
                >
                  {activeReminderCount > 9 ? '9+' : activeReminderCount}
                </motion.span>
              )}
              {!isSubscribed && (
                <span className="absolute -bottom-1 -right-1 w-3 h-3 bg-yellow-400 rounded-full animate-pulse" />
              )}
            </motion.button>

            {/* User Avatar / Menu Toggle */}
            <motion.button
              className="p-2 hover:bg-gray-100 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500"
              onClick={toggleMenu}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {isMenuOpen ? (
                <X className="w-5 h-5 text-gray-600" />
              ) : (
                <Menu className="w-5 h-5 text-gray-600" />
              )}
            </motion.button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            className="absolute top-full left-0 right-0 bg-white border-b border-gray-200 shadow-lg z-40"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            <div className="px-4 py-2">
              {/* User Info */}
              <div className="flex items-center space-x-3 py-3 border-b border-gray-100">
                <div className="w-10 h-10 bg-gradient-manga rounded-full flex items-center justify-center">
                  <span className="text-white font-bold">
                    {user ? user.name.charAt(0).toUpperCase() : '?'}
                  </span>
                </div>
                <div>
                  <p className="font-medium text-gray-800">{user ? user.name : 'Invitado'}</p>
                  <p className="text-sm text-gray-500">{user ? user.email : 'No has iniciado sesión'}</p>
                </div>
              </div>

              {/* Menu Items */}
              <nav className="py-2">
                {menuItems.map((item, index) => (
                  <motion.button
                    key={item.path}
                    className="w-full flex items-center space-x-3 px-3 py-3 text-left hover:bg-gray-50 rounded-lg transition-colors"
                    onClick={() => {
                      navigate(item.path);
                      setIsMenuOpen(false);
                    }}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    whileHover={{ x: 5 }}
                  >
                    <item.icon className="w-5 h-5 text-gray-500" />
                    <span className="text-gray-700">{item.label}</span>
                  </motion.button>
                ))}
              </nav>

              {/* Connection Status */}
              <div className="border-t border-gray-100 pt-3 mt-2">
                <div className="flex items-center justify-between px-3 py-2">
                  <span className="text-sm text-gray-500">
                    {t('app.connectionStatus')}
                  </span>
                  <div className="flex items-center space-x-2">
                    {isConnected ? (
                      <span className="text-sm text-green-600 font-medium">
                        {t('app.online')}
                      </span>
                    ) : (
                      <span className="text-sm text-red-600 font-medium">
                        {t('app.offline')}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Logout */}
              {user && (
                <div className="border-t border-gray-100 pt-2 mt-2 pb-2">
                  <button
                    className="w-full flex items-center space-x-3 px-3 py-3 text-left text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    onClick={() => {
                      localStorage.removeItem('auth_token');
                      localStorage.removeItem('user');
                      window.location.href = '/';
                    }}
                  >
                    <LogOut className="w-5 h-5" />
                    <span>Cerrar sesión</span>
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Date Selector Modal */}
      <AnimatePresence>
        {isDateSelectorOpen && (
          <DateSelector
            selectedDate={selectedDate}
            onDateSelect={handleDateChange}
            onClose={() => setIsDateSelectorOpen(false)}
          />
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;